import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { expensesAPI, glAccountsAPI, jobsAPI, uploadAPI, mercuryAPI, vendorsAPI, budgetItemsAPI, downloadFileBlob, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../components/ui/command";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Camera, UploadSimple, Spinner, Plus, Trash, Receipt, ArrowLeft, PaperPlaneRight, MagnifyingGlass, Check, Image, X, XCircle, FilePdf, PencilSimple } from "@phosphor-icons/react";
import { formatDate } from "../lib/formatDate";
import { formatCurrency } from "../lib/formatCurrency";
import { SearchableSelect } from "../components/SearchableSelect";
import { buildGlItems } from "../lib/glAccounts";
import { toast } from "sonner";

// Formula-capable amount input: type expressions like "100*1.1" and get 110 on blur
function FormulaInput({ value, onChange, className, ...props }) {
  const [display, setDisplay] = useState(String(value ?? ""));
  const [isFormula, setIsFormula] = useState(false);

  // Sync external value changes
  useEffect(() => {
    if (!isFormula) setDisplay(String(value ?? ""));
  }, [value, isFormula]);

  const handleChange = (e) => {
    const raw = e.target.value;
    setDisplay(raw);
    // Check if it's a plain number or a formula
    const cleaned = raw.replace(/\s/g, "");
    if (/^-?\d*\.?\d*$/.test(cleaned)) {
      setIsFormula(false);
      onChange(cleaned);
    } else {
      setIsFormula(true);
    }
  };

  const evaluateFormula = () => {
    if (!isFormula) return;
    const expr = display.replace(/\s/g, "");
    // Only allow safe math characters: digits, ., +, -, *, /, (, )
    if (!/^[\d.+\-*/() ]+$/.test(expr)) {
      toast.error("Invalid formula");
      setIsFormula(false);
      return;
    }
    try {
      // eslint-disable-next-line no-eval
      const result = Function('"use strict"; return (' + expr + ')')();
      if (typeof result === "number" && isFinite(result)) {
        const rounded = Math.round(result * 100) / 100;
        setDisplay(String(rounded));
        onChange(String(rounded));
        setIsFormula(false);
      } else {
        toast.error("Invalid formula result");
        setIsFormula(false);
      }
    } catch {
      toast.error("Could not evaluate formula");
      setIsFormula(false);
    }
  };

  return (
    <Input
      type="text"
      value={display}
      onChange={handleChange}
      onBlur={evaluateFormula}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); evaluateFormula(); } }}
      className={className}
      {...props}
    />
  );
}

export default function NewExpensePage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const isEdit = Boolean(id);
  const fromApprovals = new URLSearchParams(location.search).get("from") === "approvals";
  // Source list to return to after Save/Submit. Routed via navigation state
  // from whichever page deep-linked here (e.g. Transactions → Allocate).
  const returnTo = location.state?.returnTo || null;
  const [expenseStatus, setExpenseStatus] = useState(null); // populated on edit load

  // Always start the form scrolled to the top — fixes the bug where opening
  // an allocate flow on mobile landed mid-page (the autoFocus on a deeper
  // field was scrolling the viewport before the layout settled).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const [form, setForm] = useState({
    vendor: "", amount: "", date: new Date().toISOString().split("T")[0],
    description: "", line_description: "", expense_type: "job",
    job_id: "", job_name: "", gl_account_id: "", gl_account_name: "",
    mercury_transaction_id: "", receipt_path: "", splits: [],
  });
  const [jobs, setJobs] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [allGlAccounts, setAllGlAccounts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Persists the id of an auto-created draft so that a subsequent submit
  // failure (e.g. user missed a job/overhead pick) doesn't create a duplicate
  // when the user retries — see the duplicate-on-retry fix in handleSave.
  // We mirror the value into a ref so concurrent reads (e.g. an in-flight
  // autosave AND an explicit Save click) see the latest id synchronously
  // without waiting for React's async state propagation. Without this, the
  // autosave + Save race could create two drafts for the same Mercury txn.
  const [draftId, setDraftIdState] = useState(null);
  const draftIdRef = useRef(null);
  const setDraftId = (id) => {
    draftIdRef.current = id;
    setDraftIdState(id);
  };
  // In-flight guard: blocks a second POST /api/expenses while a first is
  // still flying. Without this, the autosave-and-Save race can fire two
  // concurrent creates that both miss the dedup on the backend (because both
  // ran before either inserted).
  const createInFlightRef = useRef(false);
  // Status indicator for the WIP autosave loop ("idle" | "saving" | "saved" | "error").
  // The autosave fires whenever the form has the minimum viable fields filled
  // in (vendor + amount + date) — it never submits, so an autosaved draft just
  // sits in "Pending Submission" until the user explicitly hits Save & Submit.
  const [autosaveStatus, setAutosaveStatus] = useState("idle");
  const autosaveTimerRef = useRef(null);
  // Snapshot of the last-saved form so we don't re-POST identical state on
  // every keystroke after the debounce fires.
  const lastSavedRef = useRef("");
  const [ocrResult, setOcrResult] = useState(null);
  const [scanningOcr, setScanningOcr] = useState(false);

  // Vendor search state
  const [vendorOpen, setVendorOpen] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendors, setVendors] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);

  // Receipt preview state
  const [receiptBlobUrl, setReceiptBlobUrl] = useState(null);
  const [receiptPreviewOpen, setReceiptPreviewOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Budget items cache: { [job_id]: [...items] }
  const [budgetItemsCache, setBudgetItemsCache] = useState({});
  const budgetCacheRef = useRef({});

  // Whether amount/date are locked (from Mercury transaction)
  const hasTransaction = Boolean(form.mercury_transaction_id);

  // Fetch budget items for a job (with caching)
  const fetchBudgetItems = useCallback(async (jobId) => {
    if (!jobId || budgetCacheRef.current[jobId]) return;
    try {
      const { data } = await budgetItemsAPI.list(jobId, true);
      budgetCacheRef.current[jobId] = data.budget_items;
      setBudgetItemsCache((prev) => ({ ...prev, [jobId]: data.budget_items }));
    } catch (e) { console.error(e); }
  }, []);

  // Hydrate budget item cache for any job already present in the form (covers the
  // "edit existing expense" path where job_id is preloaded without firing onChange).
  useEffect(() => {
    const jobIds = new Set();
    if (form.job_id) jobIds.add(form.job_id);
    (form.splits || []).forEach((s) => { if (s.job_id) jobIds.add(s.job_id); });
    jobIds.forEach((jid) => fetchBudgetItems(jid));
  }, [form.job_id, form.splits, fetchBudgetItems]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsRes, glRes] = await Promise.all([jobsAPI.list(), glAccountsAPI.list()]);
        setJobs(jobsRes.data.jobs);
        setAllGlAccounts(glRes.data.accounts);
        setGlAccounts(glRes.data.accounts.filter((a) => a.visible !== false));
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  const [rejectionReason, setRejectionReason] = useState(""); // shown on edit if status==='rejected'

  // Load existing expense for edit
  useEffect(() => {
    if (isEdit) {
      const loadExpense = async () => {
        try {
          const { data } = await expensesAPI.get(id);
          setForm({
            vendor: data.vendor || "", amount: data.amount || "",
            date: data.date || "", description: data.description || "",
            line_description: data.line_description || "",
            expense_type: data.expense_type || "job",
            job_id: data.job_id || "", job_name: data.job_name || "",
            budget_item_id: data.budget_item_id || "",
            budget_item_name: data.budget_item_name || "",
            budget_item_group: data.budget_item_group || "",
            unit_of_measure: data.unit_of_measure || "",
            quantity: data.quantity || "",
            gl_account_id: data.gl_account_id || "", gl_account_name: data.gl_account_name || "",
            mercury_transaction_id: data.mercury_transaction_id || "",
            receipt_path: data.receipt_path || "",
            // Ensure each split has a stable client-side _uid for React keys
            splits: (data.splits || []).map((s) => ({ ...s, _uid: s._uid || crypto.randomUUID() })),
          });
          if (data.status === "rejected" && data.rejection_reason) {
            setRejectionReason(data.rejection_reason);
          } else {
            setRejectionReason("");
          }
          setExpenseStatus(data.status || null);
        } catch (e) { toast.error("Failed to load expense"); }
      };
      loadExpense();
    }
  }, [id, isEdit]);

  // ─── WIP Autosave ────────────────────────────────────────────────────────
  // Debounced background save: persists the form as a draft 2 seconds after
  // the last edit, so the user's work survives a tab close, browser crash, or
  // accidental nav-away. Skipped while:
  //   • the user is editing an existing record (URL-edit mode handles its own save)
  //   • the form doesn't yet have the bare minimum (vendor + amount + date)
  //   • the user is in the middle of an explicit Save & Submit (saving=true)
  // First save creates the draft; subsequent saves PUT the same draftId.
  useEffect(() => {
    if (isEdit) return; // edit mode persists via explicit Save button
    if (saving) return; // don't race with the explicit handler
    if (!form.amount || !form.date) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      const snapshot = JSON.stringify({ ...form, amount: parseFloat(form.amount) || 0 });
      if (snapshot === lastSavedRef.current) return; // nothing meaningful changed
      // Don't race with an in-flight explicit Save — that path also creates
      // the draft and we'd end up double-creating.
      if (createInFlightRef.current) return;
      try {
        setAutosaveStatus("saving");
        const payload = {
          ...form,
          amount: parseFloat(form.amount),
          quantity: form.quantity === "" || form.quantity === null || form.quantity === undefined ? null : parseFloat(form.quantity),
          splits: form.splits.map((s) => ({ ...s, amount: parseFloat(s.amount) || 0 })),
        };
        // Read the ref so we always see the latest id, even if React's
        // setDraftId() from a previous autosave hasn't propagated yet.
        const currentDraft = draftIdRef.current;
        if (currentDraft) {
          await expensesAPI.update(currentDraft, payload);
        } else {
          createInFlightRef.current = true;
          try {
            const { data } = await expensesAPI.create(payload);
            setDraftId(data.id);
            if (form.mercury_transaction_id) {
              // Fire-and-forget — link the parent txn to this new draft
              mercuryAPI.link(form.mercury_transaction_id, data.id).catch(() => {});
            }
          } finally {
            createInFlightRef.current = false;
          }
        }
        lastSavedRef.current = snapshot;
        setAutosaveStatus("saved");
      } catch (e) {
        setAutosaveStatus("error");
      }
    }, 2000);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  }, [form, isEdit, saving, draftId]);

  // Pre-fill from Mercury transaction
  useEffect(() => {
    const txn = location.state?.transaction;
    if (txn) {
      // Use the real bank description from Mercury; fall back to note → merchant if absent.
      const bankDesc = txn.bank_description || txn.note || txn.merchant || "";
      // Vendor is intentionally NOT prefilled from `txn.merchant` — the bank
      // merchant is just a freeform string from the swipe (e.g. "INVOICE CLOUD")
      // and isn't a validated vendor. Auto-prefilling it used to silently leak
      // raw merchant names into the vendor list at submit time. We only
      // prefill from `suggested_vendor_name`, which is a real QBO vendor
      // matched by the AI suggestion pass.
      setForm((prev) => ({
        ...prev,
        vendor: txn.suggested_vendor_name || "",
        amount: txn.amount || "",
        date: txn.date || prev.date,
        mercury_transaction_id: txn.id || "",
        description: bankDesc,
      }));
    }
  }, [location.state]);

  // Vendor search
  const searchVendors = useCallback(async (q) => {
    setVendorLoading(true);
    try {
      const { data } = await vendorsAPI.search(q || "");
      setVendors(data.vendors);
    } catch (e) { console.error(e); }
    setVendorLoading(false);
  }, []);

  useEffect(() => {
    if (vendorOpen) searchVendors(vendorSearch);
  }, [vendorSearch, vendorOpen, searchVendors]);

  const handleSelectVendor = (vendorName) => {
    setForm((prev) => ({ ...prev, vendor: vendorName }));
    setVendorOpen(false);
  };

  const handleCreateVendor = async () => {
    if (!vendorSearch.trim()) return;
    try {
      // is_new=true so the approver sees a "NEW VENDOR" badge and confirms the
      // vendor isn't a duplicate before the expense is pushed to QBO.
      const { data } = await vendorsAPI.create({ name: vendorSearch.trim(), is_new: true });
      setForm((prev) => ({ ...prev, vendor: data.name }));
      toast.success(`Vendor "${data.name}" added — approver will verify before QBO sync`);
      setVendorOpen(false);
    } catch (e) {
      toast.error("Failed to create vendor");
    }
  };

  // Receipt upload + preview
  // Upload lands the file fast (skip_ocr=true, ~1-2s). We then kick off OCR
  // in the background so the user can start filling in the expense — OCR
  // populates any *empty* fields when it returns and never overwrites what
  // the user has already typed.
  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setOcrResult(null);
    try {
      const { data } = await uploadAPI.receipt(file);
      setForm((prev) => ({ ...prev, receipt_path: data.storage_path }));
      // Create local preview from the file
      const localUrl = URL.createObjectURL(file);
      setReceiptBlobUrl(localUrl);
      toast.success("Receipt uploaded");
      // Kick off OCR in the background — best-effort, non-blocking.
      const storagePath = data.storage_path;
      setScanningOcr(true);
      uploadAPI.scanReceipt(storagePath)
        .then(({ data: ocrRes }) => {
          const ocr = ocrRes?.ocr;
          if (!ocr || ocr.error || ocr.skipped) {
            // Silent — receipt is safely attached. User fills manually.
            return;
          }
          setOcrResult(ocr);
          // Only fill fields the user hasn't touched yet — never overwrite.
          setForm((prev) => {
            const next = { ...prev };
            if (!prev.vendor && ocr.vendor) next.vendor = ocr.vendor;
            if (!prev.description && ocr.description) next.description = ocr.description;
            // Amount / date are locked when tied to a Mercury transaction —
            // only auto-fill when the field is empty AND not Mercury-locked.
            if (!hasTransaction) {
              if (!prev.amount && ocr.amount) next.amount = String(ocr.amount);
              if (ocr.date && prev.date === new Date().toISOString().split("T")[0]) {
                // Only overwrite the default "today" date, not a user-picked one.
                next.date = ocr.date;
              }
            }
            return next;
          });
          toast.success("Receipt scanned", { description: "AI filled in what it could" });
        })
        .catch(() => {
          // Silent — the receipt is safely attached; user can fill manually.
        })
        .finally(() => setScanningOcr(false));
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Load receipt preview from storage if we have a path but no blob
  useEffect(() => {
    if (form.receipt_path && !receiptBlobUrl) {
      setLoadingPreview(true);
      downloadFileBlob(form.receipt_path)
        .then((url) => setReceiptBlobUrl(url))
        .catch(() => {})
        .finally(() => setLoadingPreview(false));
    }
  }, [form.receipt_path, receiptBlobUrl]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (receiptBlobUrl) URL.revokeObjectURL(receiptBlobUrl); };
  }, [receiptBlobUrl]);

  const handleJobChange = (jobId) => {
    const job = jobs.find((j) => j.id === jobId);
    setForm((prev) => ({ ...prev, job_id: jobId, job_name: job?.name || "", budget_item_id: "", budget_item_name: "", unit_of_measure: "", quantity: "" }));
    fetchBudgetItems(jobId);
  };

  const handleGLChange = (accountId) => {
    const account = glAccounts.find((a) => a.id === accountId);
    setForm((prev) => ({ ...prev, gl_account_id: accountId, gl_account_name: account?.name || "" }));
  };

  const addSplit = () => {
    setForm((prev) => {
      const blank = () => ({ _uid: crypto.randomUUID(), type: "job", job_id: "", job_name: "", gl_account_id: "", gl_account_name: "", budget_item_id: "", budget_item_name: "", budget_item_group: "", unit_of_measure: "", quantity: "", amount: "", description: "" });
      // If this is the first split, convert the existing single-line coding into split row 0
      if (prev.splits.length === 0) {
        // Read amount from `prev.amount` (latest state) rather than the
        // closure-captured `totalAmount` — guards against the case where the
        // user clicks "Add Split" before the Mercury-prefill effect has
        // committed. Also handles negatives (refunds) and zero correctly.
        const prevAmtNum = parseFloat(prev.amount);
        const hasPrevAmt = prev.amount !== "" && prev.amount !== null && prev.amount !== undefined && !isNaN(prevAmtNum);
        const firstRow = {
          _uid: crypto.randomUUID(),
          type: prev.expense_type || "job",
          job_id: prev.job_id || "",
          job_name: prev.job_name || "",
          gl_account_id: prev.gl_account_id || "",
          gl_account_name: prev.gl_account_name || "",
          budget_item_id: prev.budget_item_id || "",
          budget_item_name: prev.budget_item_name || "",
          budget_item_group: prev.budget_item_group || "",
          unit_of_measure: prev.unit_of_measure || "",
          quantity: prev.quantity || "",
          amount: hasPrevAmt ? String(prevAmtNum) : "",
          description: "",
        };
        return {
          ...prev,
          splits: [firstRow, blank()],
        };
      }
      return {
        ...prev,
        splits: [...prev.splits, blank()],
      };
    });
  };

  const updateSplit = (index, field, value) => {
    setForm((prev) => {
      const splits = [...prev.splits];
      splits[index] = { ...splits[index], [field]: value };
      // CRITICAL: flipping Type must scrub the OPPOSITE side's fields.
      // Previously (production bug 2026-09-28) a stale `job_id` lingered
      // on a row the user had flipped from Job → Overhead, which pushed
      // the overhead portion to the Holsinger job in both QBO and JT.
      if (field === "type") {
        if (value === "gl") {
          splits[index].job_id = "";
          splits[index].job_name = "";
          splits[index].budget_item_id = "";
          splits[index].budget_item_name = "";
          splits[index].budget_item_group = "";
          splits[index].unit_of_measure = "";
          splits[index].quantity = "";
        } else if (value === "job") {
          splits[index].gl_account_id = "";
          splits[index].gl_account_name = "";
        }
      }
      // When job changes in a split, resolve name, clear budget item, fetch items
      if (field === "job_id") {
        const job = jobs.find((j) => j.id === value);
        splits[index].job_name = job?.name || "";
        splits[index].budget_item_id = "";
        splits[index].budget_item_name = "";
        splits[index].budget_item_group = "";
        splits[index].unit_of_measure = "";
        splits[index].quantity = "";
        if (value) fetchBudgetItems(value);
      }
      // When GL account changes, resolve name
      if (field === "gl_account_id") {
        const acc = glAccounts.find((a) => a.id === value);
        splits[index].gl_account_name = acc?.name || "";
      }
      // When budget item changes, set name, UM, and group
      if (field === "budget_item_id") {
        const jobId = splits[index].job_id;
        const items = budgetItemsCache[jobId] || [];
        const item = items.find((bi) => bi.id === value);
        splits[index].budget_item_name = item?.name || "";
        splits[index].unit_of_measure = item?.unit_of_measure || "";
        splits[index].budget_item_group = item?.group_path || "";
      }
      return { ...prev, splits };
    });
  };

  const removeSplit = (index) => {
    setForm((prev) => ({ ...prev, splits: prev.splits.filter((_, i) => i !== index) }));
  };

  // Allocation calculations
  const totalAmount = parseFloat(form.amount) || 0;
  // Whether the header amount has any meaningful value (incl. negative refunds
  // and 0). Drives the read-only mirror in the single-line allocation row and
  // the allocation tracker — both used to gate on `> 0`, which made the field
  // appear blank for negative refunds and pending $0 swipes.
  const hasAmount = form.amount !== "" && form.amount !== null && form.amount !== undefined && !isNaN(parseFloat(form.amount));
  const allocatedAmount = form.splits.length > 0
    ? form.splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0)
    : totalAmount;
  const remainingAmount = totalAmount - allocatedAmount;
  const isFullyAllocated = form.splits.length === 0 || Math.abs(remainingAmount) < 0.01;

  const handleSave = async (andSubmit = false) => {
    // Save (draft): only `amount` + `date` are needed so partial WIP isn't lost.
    // Submit: vendor is also required (validated against the vendor list on the
    // backend to prevent raw bank merchant names from leaking through).
    if (!form.amount || !form.date) {
      toast.error("Please fill in amount and date");
      return;
    }
    if (andSubmit && !form.vendor) {
      toast.error("Vendor is required to submit. Pick one from the list or 'Add new vendor'.");
      return;
    }
    if (form.splits.length > 0 && !isFullyAllocated) {
      toast.error(`$${Math.abs(remainingAmount).toFixed(2)} ${remainingAmount > 0 ? "remaining" : "over-allocated"}. All funds must be allocated.`);
      return;
    }
    // On submit (not draft save): every Job-coded coding row needs a Budget Item so it can sync to JobTread.
    if (andSubmit) {
      // 1) Each coding row needs a coded target (job_id for 'job' type, gl_account_id for 'gl' type).
      const allRows = form.splits.length > 0
        ? form.splits.map((s, i) => ({ idx: i + 1, row: s }))
        : [{ idx: 1, row: { type: form.expense_type, job_id: form.job_id, gl_account_id: form.gl_account_id, budget_item_id: form.budget_item_id } }];
      const missingCoding = allRows.filter(({ row }) => {
        const t = row.type;
        if (t === "job") return !(row.job_id || "").trim();
        if (t === "gl") return !(row.gl_account_id || "").trim();
        return true; // no type selected at all
      });
      if (missingCoding.length > 0) {
        const isSingle = allRows.length === 1;
        toast.error(
          isSingle
            ? "Pick a Job or Overhead Account before submitting."
            : `Coding incomplete on row #${missingCoding.map((m) => m.idx).join(", #")} — pick a Job or Overhead Account.`
        );
        return;
      }
      // 2) Job-coded rows also need a Budget Item so the JobTread sync works.
      const missingBudget = allRows.filter(({ row }) => row.type === "job" && row.job_id && !(row.budget_item_id || "").trim());
      if (missingBudget.length > 0) {
        const label = missingBudget.length === 1 && allRows.length === 1 ? "" : ` (row #${missingBudget.map((m) => m.idx).join(", #")})`;
        toast.error(`Pick a Budget Item${label} — Job-coded expenses sync to JobTread, which requires a budget item.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        quantity: form.quantity === "" || form.quantity === null || form.quantity === undefined ? null : parseFloat(form.quantity),
        splits: form.splits.map((s) => ({ ...s, amount: parseFloat(s.amount) || 0 })),
      };
      // Effective id: use the URL-edit id if present, OR the draft id we
      // captured from a previous save on this same form session. Read from the
      // ref so we always see the latest id even if a concurrent autosave just
      // created the draft and React's state hasn't propagated yet.
      const effectiveId = id || draftIdRef.current || draftId;
      let expenseId = effectiveId;
      if (effectiveId) {
        await expensesAPI.update(effectiveId, payload);
        toast.success(expenseStatus === "submitted" ? "Approval edits saved" : "Expense updated");
      } else if (createInFlightRef.current) {
        // An autosave is in flight that will create the draft. Wait a moment
        // for it to finish, then patch the newly-created draft. Worst-case
        // bound is the autosave's network round-trip.
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const newlyCreated = draftIdRef.current;
        if (newlyCreated) {
          expenseId = newlyCreated;
          await expensesAPI.update(newlyCreated, payload);
          toast.success("Expense updated");
        } else {
          // Autosave didn't land in time — fall through to a fresh create.
          createInFlightRef.current = true;
          try {
            const { data } = await expensesAPI.create(payload);
            expenseId = data.id;
            setDraftId(expenseId);
            toast.success("Expense created");
            if (form.mercury_transaction_id) {
              await mercuryAPI.link(form.mercury_transaction_id, expenseId);
            }
          } finally {
            createInFlightRef.current = false;
          }
        }
      } else {
        createInFlightRef.current = true;
        try {
          const { data } = await expensesAPI.create(payload);
          expenseId = data.id;
          // Remember the new draft id so subsequent retries on this form patch
          // the same record instead of inserting a new one.
          setDraftId(expenseId);
          toast.success("Expense created");
          if (form.mercury_transaction_id) {
            await mercuryAPI.link(form.mercury_transaction_id, expenseId);
          }
        } finally {
          createInFlightRef.current = false;
        }
      }
      if (andSubmit && expenseId) {
        await expensesAPI.submit(expenseId);
        toast.success("Expense submitted for approval");
      }
      // Return to the page that opened this form. Priority:
      //   1. Explicit `returnTo` (set by Transactions → Allocate so the
      //      cardholder keeps working the same filtered list).
      //   2. `?from=approvals` for the approvals-queue edit flow.
      //   3. Fall back to the Expenses list.
      if (returnTo?.path) {
        navigate(returnTo.path, { state: returnTo.state || {} });
      } else if (fromApprovals) {
        navigate("/approvals");
      } else {
        navigate("/expenses");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save expense");
    } finally {
      setSaving(false);
    }
  };

  // Check if vendor exists in the list
  const vendorExistsInSearch = vendors.some(
    (v) => v.name.toLowerCase() === vendorSearch.toLowerCase()
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl" data-testid="new-expense-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-zinc-100 rounded-sm transition-colors" data-testid="back-btn">
          <ArrowLeft size={18} className="text-zinc-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {isEdit ? "Edit Expense" : "New Expense"}
          </h1>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-sm text-zinc-500">Capture and code your expense</p>
            {!isEdit && (
              <span
                data-testid="autosave-indicator"
                className={`text-[10px] uppercase tracking-wider font-bold transition-opacity ${
                  autosaveStatus === "idle" ? "opacity-0" : "opacity-100"
                } ${
                  autosaveStatus === "saving" ? "text-amber-500"
                  : autosaveStatus === "saved" ? "text-emerald-600"
                  : autosaveStatus === "error" ? "text-red-500"
                  : "text-zinc-400"
                }`}
                title="Your work is autosaved every 2 seconds — close the tab any time and it'll be waiting in Pending Submission."
              >
                {autosaveStatus === "saving" && "● Saving draft…"}
                {autosaveStatus === "saved" && "✓ Draft saved"}
                {autosaveStatus === "error" && "Autosave failed"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Rejection reason banner — shown when re-editing a rejected expense */}
      {rejectionReason && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-sm p-4 flex items-start gap-3" data-testid="rejection-banner">
          <XCircle size={18} weight="duotone" className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider font-bold text-red-700">Sent back for re-coding</p>
            <p className="text-sm text-red-800 mt-1 leading-snug">{rejectionReason}</p>
            <p className="text-[11px] text-red-500 mt-2">Fix the issue below, then click <span className="font-semibold">Save & Submit</span> to send it back for approval.</p>
          </div>
        </div>
      )}

      {/* Approver-review banner — admins/approvers editing a submitted expense from the approval queue */}
      {expenseStatus === "submitted" && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-sm p-4 flex items-start gap-3" data-testid="approver-review-banner">
          <PencilSimple size={18} weight="duotone" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider font-bold text-[#002FA7]">Approval review</p>
            <p className="text-sm text-blue-900 mt-1 leading-snug">You're editing this submitted expense before approval. Fix coding mistakes, attach additional receipts, then <span className="font-semibold">Save Changes</span>. The expense stays in the approval queue so you (or another approver) can approve it after.</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Receipt Upload + Preview */}
        <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5">
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400 mb-3">Receipt</h3>
          <div className="flex gap-3 items-start">
            <input type="file" ref={fileInputRef} accept="image/*,.pdf" className="hidden" onChange={(e) => handleUpload(e.target.files[0])} />
            <input type="file" ref={cameraInputRef} accept="image/*" capture="environment" className="hidden" onChange={(e) => handleUpload(e.target.files[0])} />

            {/* Receipt thumbnail preview */}
            {receiptBlobUrl ? (
              (() => {
                const isPdf = (form.receipt_path || "").toLowerCase().endsWith(".pdf");
                return (
                  <button
                    onClick={() => setReceiptPreviewOpen(true)}
                    data-testid="receipt-preview-thumb"
                    className="w-16 h-16 rounded-sm border border-zinc-200 overflow-hidden flex-shrink-0 hover:border-[#002FA7] hover:shadow-md transition-all duration-200 cursor-pointer relative group"
                  >
                    {isPdf ? (
                      <div className="w-full h-full flex items-center justify-center bg-rose-50">
                        <FilePdf size={28} weight="duotone" className="text-rose-600" />
                      </div>
                    ) : (
                      <img src={receiptBlobUrl} alt="Receipt" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <MagnifyingGlass size={16} weight="bold" className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                );
              })()
            ) : loadingPreview ? (
              <div className="w-16 h-16 rounded-sm border border-zinc-200 flex items-center justify-center flex-shrink-0 bg-zinc-50">
                <Spinner size={16} className="animate-spin text-zinc-400" />
              </div>
            ) : null}

            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => cameraInputRef.current?.click()} disabled={uploading}
                  data-testid="camera-upload-btn"
                  className="rounded-sm border-zinc-200 gap-1.5 text-xs h-8">
                  <Camera size={14} weight="duotone" />
                  Camera
                </Button>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  data-testid="file-upload-btn"
                  className="rounded-sm border-zinc-200 gap-1.5 text-xs h-8">
                  <UploadSimple size={14} weight="duotone" />
                  Upload
                </Button>
              </div>
              {uploading && (
                <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="uploading-indicator">
                  <Spinner size={14} className="animate-spin" />
                  Uploading receipt...
                </div>
              )}
              {!uploading && scanningOcr && (
                <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="scanning-ocr-indicator">
                  <Spinner size={14} className="animate-spin" />
                  Scanning receipt with AI...
                </div>
              )}
              {form.receipt_path && !uploading && !scanningOcr && (
                <div className="flex items-center gap-1.5">
                  <Receipt size={12} className="text-emerald-500" />
                  <span className="text-[11px] text-emerald-600 font-medium">Receipt attached</span>
                </div>
              )}
            </div>
          </div>
          {ocrResult && !ocrResult.error && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-sm p-3">
              <p className="text-[10px] uppercase tracking-widest text-blue-500 font-bold mb-1">AI Extracted</p>
              <p className="text-xs text-blue-700">
                {ocrResult.vendor} | {formatCurrency(ocrResult.amount)} | {formatDate(ocrResult.date)}
              </p>
            </div>
          )}
        </div>

        {/* Receipt Full Preview Dialog */}
        <Dialog open={receiptPreviewOpen} onOpenChange={setReceiptPreviewOpen}>
          <DialogContent className="max-w-2xl p-2 rounded-sm" data-testid="receipt-preview-dialog">
            {receiptBlobUrl && (
              (form.receipt_path || "").toLowerCase().endsWith(".pdf") ? (
                <iframe src={receiptBlobUrl} title="Receipt PDF preview" className="w-full h-[80vh] rounded-sm border-0" />
              ) : (
                <img src={receiptBlobUrl} alt="Receipt full preview" className="w-full h-auto max-h-[80vh] object-contain rounded-sm" />
              )
            )}
          </DialogContent>
        </Dialog>

        {/* Transaction Details */}
        <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5 space-y-4">
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400 mb-1">Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Vendor - searchable combobox */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Vendor</Label>
              <Popover open={vendorOpen} onOpenChange={setVendorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={vendorOpen}
                    data-testid="vendor-combobox"
                    className="mt-1 w-full justify-between rounded-sm font-normal text-sm h-9"
                  >
                    {form.vendor || "Search vendor..."}
                    <MagnifyingGlass size={14} className="ml-2 text-zinc-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-sm" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search vendors..."
                      value={vendorSearch}
                      onValueChange={setVendorSearch}
                      data-testid="vendor-search-input"
                    />
                    <CommandList>
                      {vendorLoading ? (
                        <div className="py-4 text-center text-xs text-zinc-400">Searching...</div>
                      ) : (
                        <>
                          <CommandEmpty>
                            <div className="py-2 text-xs text-zinc-500">No vendor found</div>
                          </CommandEmpty>
                          <CommandGroup>
                            {vendors.map((v) => (
                              <CommandItem
                                key={v.id}
                                value={v.name}
                                onSelect={() => handleSelectVendor(v.name)}
                                data-testid={`vendor-option-${v.id}`}
                                className="text-sm"
                              >
                                <Check size={14} className={`mr-2 ${form.vendor === v.name ? "opacity-100" : "opacity-0"}`} />
                                {v.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          {/* Create new vendor option — explicit "NEW VENDOR" wording so the
                              submitter knows the approver will be asked to verify it isn't
                              a duplicate before the expense pushes to QuickBooks. */}
                          {vendorSearch.trim() && !vendorExistsInSearch && (
                            <CommandGroup>
                              <CommandItem
                                onSelect={handleCreateVendor}
                                data-testid="create-vendor-option"
                                className="text-sm text-[#002FA7] font-medium"
                              >
                                <Plus size={14} className="mr-2" />
                                Add new vendor: "{vendorSearch.trim()}"
                                <span className="ml-auto text-[9px] uppercase tracking-wider bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-sm font-bold">NEW</span>
                              </CommandItem>
                            </CommandGroup>
                          )}
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Amount - read-only, from card swipe */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Amount ($)</Label>
              <Input
                data-testid="amount-input"
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => !hasTransaction && setForm({ ...form, amount: e.target.value })}
                readOnly={hasTransaction}
                placeholder="0.00"
                className={`mt-1 rounded-sm font-mono ${hasTransaction ? "bg-zinc-50 text-zinc-600 cursor-not-allowed" : ""}`}
              />
              {hasTransaction && (
                <p className="text-[10px] text-zinc-400 mt-0.5">Amount from card transaction</p>
              )}
            </div>

            {/* Transaction Date - static display, no interaction */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Transaction Date</Label>
              <div
                data-testid="date-input"
                className="mt-1 h-9 flex items-center px-3 rounded-sm bg-zinc-50 border border-zinc-200 text-sm text-zinc-600 cursor-default select-none"
              >
                {formatDate(form.date) || "—"}
              </div>
              <p className="text-[10px] text-zinc-400 mt-0.5">Date from card transaction</p>
            </div>

            {/* empty cell for grid alignment */}
            <div />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
              Transaction Description
              {hasTransaction && <span className="ml-2 text-[10px] text-zinc-400 normal-case font-normal tracking-normal">(from bank — read only)</span>}
            </Label>
            <Textarea
              data-testid="description-input"
              value={form.description}
              onChange={hasTransaction ? undefined : (e) => setForm({ ...form, description: e.target.value })}
              readOnly={hasTransaction}
              placeholder={hasTransaction ? "" : "Description from bank transaction"}
              className={`mt-1 rounded-sm h-20 resize-none ${hasTransaction ? "bg-zinc-50 text-zinc-600 cursor-not-allowed" : ""}`}
            />
          </div>
        </div>

        {/* Coding - now acts as default single-line allocation */}
        <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400">Allocation</h3>
            {/* Allocation tracker */}
            {totalAmount !== 0 || hasAmount ? (
              <div className="flex items-center gap-3 text-xs">
                <span className="text-zinc-400">
                  Total: <span className="font-mono font-bold text-zinc-700">${totalAmount.toFixed(2)}</span>
                </span>
                {form.splits.length > 0 && (
                  <>
                    <span className="text-zinc-300">|</span>
                    <span className="text-zinc-400">
                      Allocated: <span className={`font-mono font-bold ${isFullyAllocated ? "text-emerald-600" : "text-amber-600"}`}>${allocatedAmount.toFixed(2)}</span>
                    </span>
                    <span className="text-zinc-300">|</span>
                    <span className={`font-mono font-bold ${remainingAmount > 0.01 ? "text-red-500" : remainingAmount < -0.01 ? "text-red-500" : "text-emerald-600"}`}>
                      {remainingAmount > 0.01 ? `$${remainingAmount.toFixed(2)} remaining` : remainingAmount < -0.01 ? `$${Math.abs(remainingAmount).toFixed(2)} over` : "Fully allocated"}
                    </span>
                  </>
                )}
              </div>
            ) : null}
          </div>

          {/* If no splits, show single-line coding */}
          {form.splits.length === 0 && (
            <div className="space-y-3">
              <div className="flex gap-3 items-end flex-wrap">
                <div className="w-24">
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Type</Label>
                  <Select value={form.expense_type} onValueChange={(v) => setForm((prev) => {
                    // Scrub the OPPOSITE side when flipping Type so a stale
                    // top-level job_id/budget_item_id doesn't leak into the
                    // backend push (production bug: a Job→Overhead flip left
                    // job_id=Holsinger set, which then showed up as the
                    // CustomerRef on the QBO Purchase and a doc on Holsinger
                    // in JobTread for a pure overhead expense).
                    if (v === "gl") {
                      return {
                        ...prev, expense_type: v,
                        job_id: "", job_name: "",
                        budget_item_id: "", budget_item_name: "", budget_item_group: "",
                        unit_of_measure: "", quantity: "",
                      };
                    }
                    return { ...prev, expense_type: v, gl_account_id: "", gl_account_name: "" };
                  })}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="expense-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="job">Job</SelectItem>
                      <SelectItem value="gl">Overhead Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[160px]">
                  {form.expense_type === "job" ? (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Job</Label>
                      <SearchableSelect
                        testId="job-select"
                        value={form.job_id}
                        onChange={handleJobChange}
                        placeholder="Select a job"
                        searchPlaceholder="Search jobs..."
                        emptyText="No jobs match."
                        triggerClassName="mt-1"
                        items={jobs.map((j) => ({
                          value: j.id,
                          label: j.name,
                          searchText: `${j.number || ""} ${j.customer || ""}`,
                          suffix: (j.number || j.customer) ? (
                            <span className="text-[10px] text-zinc-400 ml-2">
                              {j.number}{j.number && j.customer ? " · " : ""}{j.customer}
                            </span>
                          ) : null,
                        }))}
                      />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Overhead Account</Label>
                      <SearchableSelect
                        testId="gl-account-select"
                        value={form.gl_account_id}
                        onChange={handleGLChange}
                        placeholder="Select overhead account"
                        searchPlaceholder="Search overhead accounts..."
                        emptyText="No overhead accounts match."
                        triggerClassName="mt-1"
                        wrap
                        items={buildGlItems(glAccounts, { allAccounts: allGlAccounts, selectedIds: [form.gl_account_id] })}
                      />
                    </div>
                  )}
                </div>
                <div className="w-28">
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Amount</Label>
                  <Input value={hasAmount ? totalAmount.toFixed(2) : ""} readOnly className="mt-1 rounded-sm font-mono bg-zinc-50 cursor-not-allowed" data-testid="single-line-amount" />
                </div>
              </div>
              {/* Budget Item row - only for job type */}
              {form.expense_type === "job" && form.job_id && (() => {
                const items = budgetItemsCache[form.job_id] || [];
                // Group items by group_path
                const grouped = {};
                items.forEach((bi) => {
                  const gp = bi.group_path;
                  if (!gp) return; // Skip ungrouped items
                  if (!grouped[gp]) grouped[gp] = [];
                  grouped[gp].push(bi);
                });
                const budgetGroups = Object.keys(grouped).map((gp) => ({
                  label: gp,
                  items: grouped[gp].map((bi) => ({
                    value: bi.id,
                    label: bi.name,
                    searchText: `${bi.unit_of_measure || ""}`,
                  })),
                }));
                return (
                <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-end">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Budget Item</Label>
                    <SearchableSelect
                      testId="budget-item-select"
                      value={form.budget_item_id || ""}
                      onChange={(v) => {
                        const item = items.find((bi) => bi.id === v);
                        setForm((prev) => ({
                          ...prev,
                          budget_item_id: v,
                          budget_item_name: item?.name || "",
                          budget_item_group: item?.group_path || "",
                          unit_of_measure: item?.unit_of_measure || "",
                        }));
                      }}
                      placeholder="Select budget item"
                      searchPlaceholder="Search budget items..."
                      emptyText="No budget items match."
                      triggerClassName="mt-1"
                      groups={budgetGroups}
                    />
                    {form.budget_item_group && (
                      <p className="text-[10px] text-zinc-400 mt-0.5 truncate">{form.budget_item_group}</p>
                    )}
                  </div>
                  {form.unit_of_measure && (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">UM</Label>
                      <div className="mt-1 h-9 w-14 flex items-center justify-center rounded-sm bg-zinc-50 border border-zinc-200 text-xs font-mono font-bold text-zinc-600" data-testid="um-display">
                        {form.unit_of_measure}
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Qty <span className="normal-case tracking-normal font-normal text-zinc-300">optional</span></Label>
                    <Input
                      data-testid="quantity-input"
                      type="number"
                      step="any"
                      value={form.quantity || ""}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      className="mt-1 rounded-sm font-mono w-20"
                    />
                  </div>
                </div>
                );
              })()}
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                  Line Description <span className="normal-case tracking-normal font-normal text-zinc-300">optional</span>
                </Label>
                <Input
                  data-testid="single-line-description"
                  value={form.line_description || ""}
                  onChange={(e) => setForm({ ...form, line_description: e.target.value })}
                  placeholder="What was this for?"
                  className="mt-1 rounded-sm h-9 text-sm"
                />
              </div>
              <p className="text-xs text-zinc-400">Full amount coded to one line. Click &quot;Add Split&quot; to allocate across multiple lines.</p>
            </div>
          )}

          {/* Split lines */}
          {form.splits.length > 0 && (
            <div className="space-y-3">
              {form.splits.map((split, i) => (
                <div key={split._uid || i} className="bg-zinc-50 p-3 rounded-sm space-y-2" data-testid={`split-row-${i}`}>
                  <div className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-end">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Type</Label>
                      <Select value={split.type || "job"} onValueChange={(v) => updateSplit(i, "type", v)}>
                        <SelectTrigger className="mt-0.5 rounded-sm h-8 text-xs w-[72px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="job">Job</SelectItem>
                          <SelectItem value="gl">Overhead</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="min-w-0">
                      <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
                        {split.type === "job" ? "Job" : "Overhead Account"}
                      </Label>
                      <SearchableSelect
                        testId={`split-target-${i}`}
                        size="sm"
                        value={split.type === "job" ? (split.job_id || "") : (split.gl_account_id || "")}
                        onChange={(v) => updateSplit(i, split.type === "job" ? "job_id" : "gl_account_id", v)}
                        placeholder="Select..."
                        searchPlaceholder={split.type === "job" ? "Search jobs..." : "Search overhead accounts..."}
                        emptyText={split.type === "job" ? "No jobs match." : "No overhead accounts match."}
                        triggerClassName="mt-0.5"
                        items={split.type === "job"
                          ? jobs.map((j) => ({
                              value: j.id,
                              label: j.name,
                              searchText: `${j.number || ""} ${j.customer || ""}`,
                              suffix: (j.number || j.customer) ? (
                                <span className="text-[9px] text-zinc-400 ml-2">{j.number}{j.number && j.customer ? " · " : ""}{j.customer}</span>
                              ) : null,
                            }))
                          : buildGlItems(glAccounts, { allAccounts: allGlAccounts, selectedIds: [split.gl_account_id] })
                        }
                        wrap={split.type !== "job"}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Amount</Label>
                      <FormulaInput
                        value={split.amount}
                        onChange={(val) => updateSplit(i, "amount", val)}
                        className="mt-0.5 rounded-sm h-8 text-xs font-mono w-24"
                        data-testid={`split-amount-${i}`}
                      />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeSplit(i)} className="h-8 w-8 p-0 text-red-400 hover:text-red-600 flex-shrink-0" data-testid={`remove-split-${i}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                  {/* Budget Item row for job-type splits */}
                  {split.type === "job" && split.job_id && (() => {
                    const splitItems = budgetItemsCache[split.job_id] || [];
                    const splitGrouped = {};
                    splitItems.forEach((bi) => {
                      const gp = bi.group_path;
                      if (!gp) return; // Skip ungrouped items
                      if (!splitGrouped[gp]) splitGrouped[gp] = [];
                      splitGrouped[gp].push(bi);
                    });
                    const splitBudgetGroups = Object.keys(splitGrouped).map((gp) => ({
                      label: gp,
                      items: splitGrouped[gp].map((bi) => ({
                        value: bi.id,
                        label: bi.name,
                        searchText: `${bi.unit_of_measure || ""}`,
                      })),
                    }));
                    return (
                    <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-end">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Budget Item</Label>
                        <SearchableSelect
                          testId={`split-budget-item-${i}`}
                          size="sm"
                          value={split.budget_item_id || ""}
                          onChange={(v) => updateSplit(i, "budget_item_id", v)}
                          placeholder="Select budget item"
                          searchPlaceholder="Search budget items..."
                          emptyText="No budget items match."
                          triggerClassName="mt-0.5"
                          groups={splitBudgetGroups}
                        />
                        {split.budget_item_group && (
                          <p className="text-[9px] text-zinc-400 mt-0.5 truncate">{split.budget_item_group}</p>
                        )}
                      </div>
                      {split.unit_of_measure && (
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">UM</Label>
                          <div className="mt-0.5 h-8 w-12 flex items-center justify-center rounded-sm bg-white border border-zinc-200 text-[11px] font-mono font-bold text-zinc-600" data-testid={`split-um-${i}`}>
                            {split.unit_of_measure}
                          </div>
                        </div>
                      )}
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Qty <span className="normal-case tracking-normal font-normal text-zinc-300">optional</span></Label>
                        <Input
                          type="number" step="any"
                          value={split.quantity || ""}
                          onChange={(e) => updateSplit(i, "quantity", e.target.value)}
                          className="mt-0.5 rounded-sm h-8 text-xs font-mono w-16"
                          data-testid={`split-qty-${i}`}
                        />
                      </div>
                    </div>
                    );
                  })()}
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
                      Description <span className="normal-case tracking-normal font-normal text-zinc-300">optional</span>
                    </Label>
                    <Input
                      value={split.description || ""}
                      onChange={(e) => updateSplit(i, "description", e.target.value)}
                      placeholder="What was this for?"
                      className="mt-0.5 rounded-sm h-8 text-xs"
                      data-testid={`split-description-${i}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <Button variant="outline" size="sm" onClick={addSplit} data-testid="add-split-btn" className="rounded-sm text-xs gap-1 border-zinc-200">
              <Plus size={12} /> Add Split Line
            </Button>
            {form.splits.length > 0 && hasAmount && totalAmount !== 0 && (
              <div className={`px-3 py-1 rounded-sm text-xs font-bold ${
                isFullyAllocated
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-red-50 text-red-600 border border-red-200"
              }`} data-testid="allocation-status">
                {isFullyAllocated
                  ? "Fully Allocated"
                  : remainingAmount > 0
                    ? `$${remainingAmount.toFixed(2)} Remaining`
                    : `$${Math.abs(remainingAmount).toFixed(2)} Over-allocated`
                }
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pb-8">
          <Button variant="outline"
            onClick={() => navigate(fromApprovals ? "/approvals" : "/expenses")}
            className="rounded-sm" data-testid="cancel-btn">
            Cancel
          </Button>
          {expenseStatus === "submitted" ? (
            // Approver editing a submitted expense — just save changes, keep status.
            <Button
              onClick={() => handleSave(false)}
              disabled={saving || (form.splits.length > 0 && !isFullyAllocated)}
              data-testid="save-changes-btn"
              className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm shadow-sm gap-1.5 hover:-translate-y-[1px] hover:shadow-md transition-all duration-200 disabled:opacity-50">
              <Check size={14} weight="bold" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          ) : (
            <>
              <Button onClick={() => handleSave(false)} disabled={saving} data-testid="save-draft-btn"
                className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm shadow-sm">
                {saving ? "Saving..." : "Save Draft"}
              </Button>
              <Button onClick={() => handleSave(true)} disabled={saving || (form.splits.length > 0 && !isFullyAllocated)} data-testid="save-submit-btn"
                className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm shadow-sm gap-1.5 hover:-translate-y-[1px] hover:shadow-md transition-all duration-200 disabled:opacity-50">
                <PaperPlaneRight size={14} />
                {saving ? "Saving..." : "Save & Submit"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
